// Lib
import React, { useRef, useMemo, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { isEmpty, defer } from 'lodash/fp';
import classNames from 'classnames';

// Actions
import { endAnnotationMode } from '../store/annotationActions';
import { deleteElement } from '../../../../../common/elements/elementActions';
import { createElementSync, atomicMoveAndUpdate } from '../../../actions/elementActions';
import { closeAllPopupsIfRequired } from '../../../../components/popupPanel/popupActions';
import { deselectAllElements } from '../../../selection/selectionActions';
import { getNewTransactionId } from '../../../../utils/undoRedo/undoRedoTransactionManager';

// Utils
import { getScore } from '../../../../../common/elements/utils/elementPropertyUtils';
import { createInitialPaths } from '../../drawingEditor/drawingEditorElementContentUtils';
import measurementsRegistry from '../../../../components/measurementsStore/measurementsRegistry';
import {
    createAnnotationElementDefinition,
    createGroupedAnnotationElementDefinitions,
    getCanvasViewportTranslation,
} from './annotationEditorUtils';

// Selectors
import {
    getIsAnnotationModeEnabled,
    getAnnotationEditedElementId,
    editedAnnotationElementSelector,
} from '../store/annotationSelector';
import { getCurrentVisibleBoardCanvasOrigin, getCurrentBoardId } from '../../../selectors/currentBoardSelector';
import { isDebugToolbarToolEnabled } from '../../../../debug/debugStateSelector';
import { isCurrentUserSettingDrawingInkEffectEnabled } from '../../../../user/currentUserSelector';
import { debugToolProperty, debugToolLastUpdateTime } from '../../../../debug/store/debugToolSelector';
import getGridSize from '../../../../utils/grid/gridSizeSelector';
import { canvasZoomScaleSelector } from '../../../../canvas/store/canvasZoomSelector';

// Components
import { DrawingEditorToolbarWithTransitions } from '../../drawingEditor/drawingEditorToolbar/DrawingEditorToolbar';
import DrawingEditorCanvas from '../../drawingEditor/drawingEditorCanvas/DrawingEditorCanvas';
import DrawingEditorDebugger from '../../drawingEditor/drawingDebugTools/DrawingEditorDebugger';
import LiveDrawingCollaborationManager from '../../liveDrawing/LiveDrawingCollaborationManager';
import LiveCursorTracker from '../../../../remoteActivity/liveCollaboration/cursorTracking/LiveCursorTracker';
import LiveDrawingCanvasRenderer from '../../liveDrawing/LiveDrawingCanvasRenderer';
import LiveCursorDisplay from '../../../../remoteActivity/liveCollaboration/cursorTracking/LiveCursorDisplay';
import CanvasViewportReplica from '../../../../canvas/dnd/CanvasViewportReplica';

// Hooks
import useDrawingEditorState from '../../drawingEditor/useDrawingEditorState';
import useBodyClass from '../../../../utils/react/useBodyClass';
import useStalledBoolean from '../../../../utils/react/useStalledBoolean';
import useDrawingEditorToolbarState from '../../drawingEditor/useDrawingEditorToolbarState';
import useDrawingEditorKeyboardEventHandler from '../../drawingEditor/useDrawingEditorKeyboardEventHandler';
import useDrawingEditorIPadState from '../../drawingEditor/useDrawingEditorIPadState';
import useLastMousePointerEventAge from '../../drawingEditor/drawingEditorCanvas/useLastMousePointerEventAge';

// Constants
import { DEBUG_TOOLS } from '../../../../debug/store/debugToolConstants';
import { TOOL_MODES } from '../../drawingEditor/drawingEditorConstants';
import { CURSOR_TYPES } from '../../../../../common/users/liveCursor/liveCursorConstants';
import { TIMES } from '../../../../../common/utils/timeUtil';
import { ElementType } from '../../../../../common/elements/elementTypes';

// Styles
import './AnnotationEditor.scss';

const getCursorType = (toolMode) => {
    switch (toolMode) {
        case TOOL_MODES.DRAW:
            return CURSOR_TYPES.PEN;
        case TOOL_MODES.ERASE:
            return CURSOR_TYPES.ERASER;
        default:
            return CURSOR_TYPES.POINTER;
    }
};

const mapStateToProps = createStructuredSelector({
    editedDrawingId: getAnnotationEditedElementId,
    editedDrawingElement: editedAnnotationElementSelector,
    gridSize: getGridSize,
    currentBoardId: getCurrentBoardId,
    canvasOrigin: getCurrentVisibleBoardCanvasOrigin,
    zoomScale: canvasZoomScaleSelector,
    enableDrawingInkEffect: isCurrentUserSettingDrawingInkEffectEnabled,
    isDebugEnabled: isDebugToolbarToolEnabled(DEBUG_TOOLS.DRAWING),
    debugUpdateAllStrokes: debugToolProperty(DEBUG_TOOLS.DRAWING, 'updateAllStrokes'),
    debugHideOverlay: debugToolProperty(DEBUG_TOOLS.DRAWING, 'hideOverlay'),
    debugDrawingLastUpdateTime: debugToolLastUpdateTime(DEBUG_TOOLS.DRAWING),
});

const mapDispatchToProps = (dispatch) => ({
    dispatchCloseAllPopups: () => dispatch(closeAllPopupsIfRequired()),
    dispatchEndDrawingMode: () => dispatch(endAnnotationMode()),
    dispatchCreateElement: ({ location, content }) =>
        dispatch(
            createElementSync({
                elementType: ElementType.ANNOTATION_TYPE,
                location,
                content,
            }),
        ),
    dispatchUpdateElement: ({ id, location, content }) => {
        const transactionId = getNewTransactionId();

        dispatch(
            atomicMoveAndUpdate({
                id,
                location,
                changes: content,
                transactionId,
            }),
        );

        dispatch(
            deselectAllElements({
                transactionId,
            }),
        );
    },
    dispatchDeleteElement: ({ id, currentBoardId }) => {
        const transactionId = getNewTransactionId();
        dispatch(
            deleteElement({
                id,
                elementType: ElementType.ANNOTATION_TYPE,
                location: {
                    parentId: currentBoardId,
                },
                sync: true,
                transactionId,
            }),
        );
    },
});

const useMountCursorTracker = () => {
    const [mountCursorTracker, setMountCursorTracker] = useState();

    useEffect(() => {
        defer(() => setMountCursorTracker(true));
    }, []);

    return mountCursorTracker;
};

let AnnotationEditor = ({
    gridSize,
    zoomScale,
    currentBoardId,
    canvasOrigin,
    enableDrawingInkEffect,

    editedDrawingId,
    editedDrawingElement,

    dispatchEndDrawingMode,
    dispatchCreateElement,
    dispatchUpdateElement,
    dispatchDeleteElement,

    isDebugEnabled,
    debugUpdateAllStrokes,
    debugHideOverlay,
    debugDrawingLastUpdateTime,
}) => {
    const svgRef = useRef();
    const onPathPropertyChangeRef = useRef();

    const [editorClassName, setEditorClassName] = useState();
    const mountCursorTracker = useMountCursorTracker();

    // get canvas translation on mount
    const canvasTranslation = useMemo(() => {
        const translation = getCanvasViewportTranslation({
            canvasViewportDomNode: measurementsRegistry.getCanvasViewportDomElement(),
            drawEditorDomNode: svgRef.current,
        });

        return translation;
    }, []);

    // Using a layout effect here to set initial paths because
    // the SVG canvas needs to exist before we can work out the scroll translation
    const initialPaths = useMemo(() => {
        const paths = createInitialPaths({
            editedDrawingElement,
            gridSize,
            canvasTranslation,
            canvasOrigin,
        });

        return paths;
    }, [editedDrawingId]);

    const {
        paths,
        storedPaths,
        currentPath,
        setPaths,
        beginPath,
        endPath,
        cancelPath,
        appendPointToCurrentPath,
        clearPaths,
        removePaths,
        undoPath,
        redoPath,
        canUndo,
        canRedo,
    } = useDrawingEditorState(initialPaths);

    const { isPenMode, setIsPenMode } = useDrawingEditorIPadState({ svgRef, undoPath, redoPath });

    const isDrawing = !!currentPath;
    const isTouchEventInToolbarBlocked = useStalledBoolean(isPenMode && isDrawing);

    const {
        toolMode,
        toolModeState,
        setToolModeState,
        strokeSize,
        strokeColor,
        colorPopupOpen,
        strokePopupOpen,
        setToolMode,
        setStrokeSize,
        setStrokeColor,
        switchToPreviousColor,
        setStrokeSizeState,
        setStrokeColorState,
        setColorPopupOpen,
        setCustomColorInputOpen,
        setStrokePopupOpen,
        closeAllPopups,
    } = useDrawingEditorToolbarState({ onPathPropertyChangeRef });

    // TODO - Remove this if we decide to proceed with the annotation editor overlay
    useBodyClass(debugHideOverlay ? 'annotation-editor' : 'annotation-editor-with-overlay');

    const clearDrawingModeState = () => {
        dispatchEndDrawingMode();
        setToolMode(TOOL_MODES.DRAW);
        clearPaths();
    };

    const exitDrawingEditorMode = () => {
        closeAllPopups();
        clearDrawingModeState();
    };

    const saveAndExitDrawingEditor = (pathsToSave) => {
        if (editedDrawingId) {
            // when updating an element, maintain z-order by using it's existing position score
            const positionScore = getScore(editedDrawingElement);

            const elementDef = createAnnotationElementDefinition({
                paths: pathsToSave,
                currentBoardId,
                gridSize,
                canvasTranslation,
                canvasOrigin,
                positionScore,
            });

            // If we've deleted all the lines then delete this drawing
            if (!elementDef) {
                dispatchDeleteElement({ id: editedDrawingId, currentBoardId });
                return exitDrawingEditorMode();
            }

            dispatchUpdateElement({
                id: editedDrawingId,
                ...elementDef,
            });
        } else {
            const elementDefs = createGroupedAnnotationElementDefinitions({
                paths: pathsToSave,
                currentBoardId,
                gridSize,
                canvasTranslation,
                canvasOrigin,
            });

            if (isEmpty(elementDefs)) return exitDrawingEditorMode();

            elementDefs.forEach((elementDef) => {
                dispatchCreateElement(elementDef);
            });
        }

        return exitDrawingEditorMode();
    };

    const handleSaveButton = () => saveAndExitDrawingEditor(paths);

    useDrawingEditorKeyboardEventHandler({
        toolMode,
        undoPath,
        redoPath,
        cancelPath,
        setToolMode,
        setStrokeSize,
        switchToPreviousColor,
        handleSaveButton,
        colorPopupOpen,
        setColorPopupOpen,
    });

    const { recordPointerEvent, getLastMouseEventAge } = useLastMousePointerEventAge();

    const onDoubleClick = useCallback(
        (event) => {
            // Don't exit on double click in iPad or when using a stylus
            if (getLastMouseEventAge() > TIMES.SECOND / 2) return;

            event.preventDefault();
            event.stopPropagation();

            saveAndExitDrawingEditor(paths);
        },
        [paths],
    );

    const drawingEditorClasses = classNames('AnnotationEditor', editorClassName, toolMode);

    return (
        <div className={drawingEditorClasses} onDoubleClick={onDoubleClick} onPointerDown={recordPointerEvent}>
            <LiveDrawingCollaborationManager
                gridSize={gridSize}
                translation={canvasTranslation}
                paths={storedPaths}
                currentPath={currentPath}
                editedDrawingId={editedDrawingId}
            />

            {mountCursorTracker && (
                <>
                    <LiveCursorTracker
                        cursorType={getCursorType(toolMode)}
                        translation={canvasTranslation}
                        eventListenerNode={svgRef.current}
                    />
                    <LiveCursorDisplay
                        translation={{
                            x: -canvasTranslation.x,
                            y: -canvasTranslation.y,
                        }}
                    />
                </>
            )}

            <LiveDrawingCanvasRenderer
                translation={{
                    x: -canvasTranslation.x,
                    y: -canvasTranslation.y,
                }}
            />

            <DrawingEditorToolbarWithTransitions
                isDrawing={isDrawing}
                isEditingExistingDrawing={!!editedDrawingElement}
                isTouchEventInToolbarBlocked={isTouchEventInToolbarBlocked}
                isPenMode={isPenMode}
                undoPath={undoPath}
                redoPath={redoPath}
                canUndo={canUndo}
                canRedo={canRedo}
                setIsPenMode={setIsPenMode}
                toolMode={toolMode}
                toolModeState={toolModeState}
                strokeSize={strokeSize}
                strokeColor={strokeColor}
                colorPopupOpen={colorPopupOpen}
                strokePopupOpen={strokePopupOpen}
                setToolMode={setToolMode}
                handleDiscardButton={exitDrawingEditorMode}
                handleSaveButton={handleSaveButton}
                handleSetStrokeSize={setStrokeSize}
                handleSetStrokeColor={setStrokeColor}
                setColorPopupOpen={setColorPopupOpen}
                setCustomColorInputOpen={setCustomColorInputOpen}
                setStrokePopupOpen={setStrokePopupOpen}
            />

            <CanvasViewportReplica>
                <DrawingEditorCanvas
                    ref={svgRef}
                    isEditable
                    onPathPropertyChangeRef={onPathPropertyChangeRef}
                    setEditorClassName={setEditorClassName}
                    isCurrentlyDrawing={!!currentPath}
                    toolMode={toolMode}
                    setToolModeState={setToolModeState}
                    paths={paths}
                    setPaths={setPaths}
                    beginPath={beginPath}
                    endPath={endPath}
                    cancelPath={cancelPath}
                    isPenMode={isPenMode}
                    appendPointToCurrentPath={appendPointToCurrentPath}
                    removePaths={removePaths}
                    strokeColor={strokeColor}
                    strokeSize={strokeSize}
                    zoomScale={zoomScale}
                    enableDrawingInkEffect={enableDrawingInkEffect}
                    setStrokeSizeState={setStrokeSizeState}
                    setStrokeColorState={setStrokeColorState}
                    closeAllPopups={closeAllPopups}
                    saveAndExitDrawingEditor={saveAndExitDrawingEditor}
                    debugUpdateAllStrokes={debugUpdateAllStrokes}
                    debugDrawingLastUpdateTime={debugDrawingLastUpdateTime}
                />
            </CanvasViewportReplica>

            {isDebugEnabled && <DrawingEditorDebugger paths={paths} gridSize={gridSize} />}
        </div>
    );
};

AnnotationEditor.propTypes = {
    drawingModeEnabled: PropTypes.bool,
    enableDrawingInkEffect: PropTypes.bool,

    zoomScale: PropTypes.number,
    gridSize: PropTypes.number,
    canvasOrigin: PropTypes.object,
    currentBoardId: PropTypes.string,
    editedDrawingId: PropTypes.string,
    editedDrawingElement: PropTypes.object,

    dispatchEndDrawingMode: PropTypes.func,
    dispatchCreateElement: PropTypes.func,
    dispatchUpdateElement: PropTypes.func,
    dispatchDeleteElement: PropTypes.func,
    dispatchCloseAllPopups: PropTypes.func,

    isDebugEnabled: PropTypes.bool,
    debugUpdateAllStrokes: PropTypes.bool,
    debugHideOverlay: PropTypes.bool,
    debugDrawingLastUpdateTime: PropTypes.number,
};

AnnotationEditor = connect(mapStateToProps, mapDispatchToProps)(AnnotationEditor);

const mapWrapperStateToProps = createStructuredSelector({
    drawingModeEnabled: getIsAnnotationModeEnabled,
});

/**
 * This component improves general performance by not attaching the
 * drawing editor hooks until we're actually in drawing editor mode.
 */
const AnnotationEditorWrapper = (props) => {
    const { drawingModeEnabled } = props;

    if (!drawingModeEnabled) return null;

    return <AnnotationEditor {...props} />;
};

AnnotationEditorWrapper.propTypes = {
    drawingModeEnabled: PropTypes.bool,
};

export default connect(mapWrapperStateToProps)(AnnotationEditorWrapper);
